import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, CalendarCheck, Plus, ArrowLeft, MapPin, Clock, Pencil, X, Download, Check, Trash2, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ title: '', member_id: '', date: '', time: '', location: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '', member_id: '', date: '', time: '', location: '', notes: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    fetchAppointments();
    fetchMembers();
  }, [token]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`, { headers });
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`, { headers });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/appointments`, formData, { headers });
      setShowForm(false);
      setFormData({ title: '', member_id: '', date: '', time: '', location: '', notes: '' });
      fetchAppointments();
    } catch (error) {
      console.error('Failed to create appointment:', error);
    }
  };

  const startEdit = (appt) => {
    setEditingId(appt.id);
    setEditData({
      title: appt.title,
      member_id: appt.member_id,
      date: appt.date,
      time: appt.time,
      location: appt.location || '',
      notes: appt.notes || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ title: '', member_id: '', date: '', time: '', location: '', notes: '' });
  };

  const saveEdit = async (apptId) => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/appointments/${apptId}`, editData, { headers });
      setEditingId(null);
      fetchAppointments();
    } catch (error) {
      console.error('Failed to update appointment:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingEdit(false);
    }
  };

  const downloadPdf = async (apptId) => {
    try {
      const response = await axios.get(`${API}/appointments/${apptId}/pdf`, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `rdv-${apptId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
    } catch (error) {
      console.error('PDF download failed:', error);
      window.open(`${API}/appointments/${apptId}/pdf?token=${token}`, '_blank');
    }
  };

  const deleteAppointment = async (apptId) => {
    if (!window.confirm('Supprimer ce rendez-vous ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/appointments/${apptId}`, { headers });
      fetchAppointments();
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? `${member.first_name} ${member.last_name}` : 'Membre inconnu';
  };

  const filteredAppointments = appointments.filter(a => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      getMemberName(a.member_id).toLowerCase().includes(q) ||
      (a.location || '').toLowerCase().includes(q) ||
      (a.date || '').includes(q) ||
      (a.notes || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading">Rendez-vous</h1>
          </div>
          <Button data-testid="add-appointment-btn" onClick={() => setShowForm(!showForm)} className="w-full sm:w-auto">
            <Plus className="w-5 h-5 mr-2" />
            {showForm ? 'Annuler' : 'Nouveau RDV'}
          </Button>
        </div>

        {!showForm && appointments.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="appointment-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par titre, bénéficiaire, lieu, date..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
            />
          </div>
        )}

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Card>
              <CardHeader><CardTitle>Nouveau rendez-vous</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Titre</label>
                    <Input data-testid="appointment-title-input" name="title" value={formData.title} onChange={handleChange} placeholder="Ex: Consultation médicale" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Bénéficiaire</label>
                    <select data-testid="appointment-member-select" name="member_id" value={formData.member_id} onChange={handleChange}
                      className="flex w-full rounded-xl border-slate-200 bg-white px-4 py-3 text-base transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none" required>
                      <option value="">Sélectionnez un bénéficiaire</option>
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>{member.first_name} {member.last_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                      <Input data-testid="appointment-date-input" type="date" name="date" value={formData.date} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Heure</label>
                      <Input data-testid="appointment-time-input" type="time" name="time" value={formData.time} onChange={handleChange} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Lieu</label>
                    <Input data-testid="appointment-location-input" name="location" value={formData.location} onChange={handleChange} placeholder="Adresse ou nom du lieu" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                    <Textarea data-testid="appointment-notes-input" name="notes" value={formData.notes} onChange={handleChange} placeholder="Informations complémentaires..." />
                  </div>
                  <Button data-testid="submit-appointment-btn" type="submit" className="w-full">Planifier le rendez-vous</Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {appointments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CalendarCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Aucun rendez-vous</h3>
              <p className="text-slate-600 mb-6">Planifiez votre premier rendez-vous</p>
              <Button data-testid="empty-add-appointment-btn" onClick={() => setShowForm(true)}>
                <Plus className="w-5 h-5 mr-2" />
                Nouveau rendez-vous
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
          {filteredAppointments.length === 0 && searchQuery ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Aucun rendez-vous ne correspond à votre recherche</p>
              </CardContent>
            </Card>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredAppointments.map((appt) => (
              <motion.div key={appt.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card data-testid={`appointment-card-${appt.id}`} className="hover:-translate-y-1">
                  <CardContent className="p-4 md:p-6">
                    {editingId === appt.id ? (
                      <div className="space-y-3">
                        <Input data-testid={`edit-appt-title-${appt.id}`} value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} placeholder="Titre" />
                        <select data-testid={`edit-appt-member-${appt.id}`} value={editData.member_id} onChange={(e) => setEditData({ ...editData, member_id: e.target.value })}
                          className="flex w-full rounded-xl border-slate-200 bg-white px-3 py-2 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none">
                          <option value="">Sélectionnez un bénéficiaire</option>
                          {members.map((m) => (
                            <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                          ))}
                        </select>
                        <div className="grid grid-cols-2 gap-3">
                          <Input data-testid={`edit-appt-date-${appt.id}`} type="date" value={editData.date} onChange={(e) => setEditData({ ...editData, date: e.target.value })} />
                          <Input data-testid={`edit-appt-time-${appt.id}`} type="time" value={editData.time} onChange={(e) => setEditData({ ...editData, time: e.target.value })} />
                        </div>
                        <Input data-testid={`edit-appt-location-${appt.id}`} value={editData.location} onChange={(e) => setEditData({ ...editData, location: e.target.value })} placeholder="Lieu" />
                        <Textarea data-testid={`edit-appt-notes-${appt.id}`} value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} placeholder="Notes" rows={3} />
                        <div className="flex gap-2">
                          <Button data-testid={`save-edit-appt-${appt.id}`} size="sm" onClick={() => saveEdit(appt.id)} disabled={savingEdit}>
                            <Check className="w-4 h-4 mr-1" />{savingEdit ? 'Sauvegarde...' : 'Sauvegarder'}
                          </Button>
                          <Button data-testid={`cancel-edit-appt-${appt.id}`} size="sm" variant="outline" onClick={cancelEdit}>
                            <X className="w-4 h-4 mr-1" />Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-4 mb-4">
                          <div className="p-3 bg-info/10 rounded-full shrink-0">
                            <CalendarCheck className="w-6 h-6 text-info" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-primary-900 mb-1">{appt.title}</h3>
                            <p className="text-sm font-medium text-secondary">{getMemberName(appt.member_id)}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button data-testid={`edit-appt-btn-${appt.id}`} onClick={() => startEdit(appt)} className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors" title="Modifier">
                              <Pencil className="w-4 h-4 text-slate-400 hover:text-primary" />
                            </button>
                            <button data-testid={`download-appt-pdf-${appt.id}`} onClick={() => downloadPdf(appt.id)} className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors" title="Télécharger PDF">
                              <Download className="w-4 h-4 text-slate-400 hover:text-primary" />
                            </button>
                            <button data-testid={`delete-appt-${appt.id}`} onClick={() => deleteAppointment(appt.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-slate-600">
                            <CalendarCheck className="w-4 h-4" />
                            <span className="text-sm">{appt.date}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">{appt.time}</span>
                          </div>
                          {appt.location && (
                            <div className="flex items-center gap-2 text-slate-600">
                              <MapPin className="w-4 h-4" />
                              <span className="text-sm">{appt.location}</span>
                            </div>
                          )}
                        </div>
                        {appt.notes && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <p className="text-sm text-slate-600">{appt.notes}</p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default AppointmentsPage;
